import { FileExcelOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
} from 'antd'
import { debounce } from 'lodash'
import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import AccessControlAction from '../../components/AccessControlAction'
import { AvatarHasName } from '../../components/Common/Avatar'
import { StatusTag } from '../../components/Common/StatusTag'
import { TableBottomPaginationBlock } from '../../components/Common/Table'
import TableComponent from '../../components/Common/TableComponent'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { ACL_ACTION_TYPE, TYPE_STATUS } from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout'
import InternalAssessmentExportExcel from './ExportExcel/InternalAssessmentExportExcel'
import InternalAssessmentCreate from './InternalAssessmentCreate/InternalAssessmentCreate'

const { Option } = Select

const InternalAssessment = props => {
  const {
    history,
    internalAssessmentStore,
    loadingAnimationStore,
    userStore,
    departmentStore,
    authenticationStore,
  } = props
  const {
    ratings,
    totalCount,
    fetchAllRatings,
    page,
    size,
    clearQueryParams,
    queryParams,
  } = internalAssessmentStore
  const [form] = Form.useForm()
  const { currentUser } = authenticationStore
  const { mentionUserList, getMentionUserList } = userStore
  const {
    getDepartmentWithUsers,
    departmentListV2,
    userInOffice,
    getDepartmentList,
    departmentListOnlyDepartment,
  } = departmentStore
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [openCreateAssessment, setOpenCreateAssessment] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const [filters, setFilters] = useState({
    title: '',
    year: null,
    month: null,
    status: null,
    departmentCode: null,
  })

  // Load dữ liệu ban đầu và thiết lập departmentCode
  useEffect(() => {
    const initializeData = async () => {
      try {
        loadingAnimationStore.showSpinner(true)

        // Tải dữ liệu song song để giảm thời gian chờ
        const [
          departmentListPromise,
          mentionUserListPromise,
          departmentWithUsersPromise,
        ] = [
          getDepartmentList(),
          getMentionUserList(),
          getDepartmentWithUsers(),
        ]

        await Promise.all([
          departmentListPromise,
          mentionUserListPromise,
          departmentWithUsersPromise,
        ])

        // Kiểm tra xem đã có departmentCode trong store chưa
        let initialDepartmentCode =
          internalAssessmentStore.queryParams.departmentCode

        // Nếu không có departmentCode trong store (lần đầu tiên), sử dụng giá trị từ currentUser
        if (
          !initialDepartmentCode &&
          currentUser?.department?.code &&
          departmentListOnlyDepartment?.length > 0
        ) {
          initialDepartmentCode = currentUser.department.code
        }

        // Khi khởi tạo, clear các filter khác và chỉ giữ lại departmentCode
        setFilters({
          title: '',
          year: null,
          month: null,
          status: null,
          departmentCode: initialDepartmentCode,
        })

        // Cập nhật queryParams trong store để gọi API
        runInAction(() => {
          internalAssessmentStore.queryParams = {
            page: 0,
            size: queryParams.size,
            departmentCode: initialDepartmentCode,
            // Xóa các filter khác trong queryParams
            title: null,
            year: null,
            month: null,
            status: null,
          }
        })

        // Gọi API fetchAllRatings sau khi queryParams đã được cập nhật
        await fetchAllRatings()
      } catch (error) {
        console.error('Error fetching initial data:', error)
      } finally {
        loadingAnimationStore.showSpinner(false)
        setIsInitialLoad(false)
      }
    }

    if (isInitialLoad) {
      initializeData()
    }
  }, [
    isInitialLoad,
    getDepartmentList,
    getMentionUserList,
    getDepartmentWithUsers,
    currentUser,
    departmentListOnlyDepartment,
    fetchAllRatings,
    internalAssessmentStore,
    loadingAnimationStore,
    queryParams.size,
  ])

  const handleSearch = useCallback(
    searchFilters => {
      const queryParams = {
        ...internalAssessmentStore.queryParams,
        page: 0,
        title: searchFilters.title || null,
        year: searchFilters.year || null,
        month: searchFilters.month || null,
        status: searchFilters.status || null,
      }

      if (searchFilters.departmentCode) {
        queryParams.departmentCode = searchFilters.departmentCode
      } else {
        delete queryParams.departmentCode
      }

      runInAction(() => {
        internalAssessmentStore.queryParams = queryParams
      })

      loadingAnimationStore.setTableLoading(true)
      fetchAllRatings().finally(() => {
        loadingAnimationStore.setTableLoading(false)
      })
    },
    [internalAssessmentStore, loadingAnimationStore, fetchAllRatings]
  )

  const debouncedSearch = useMemo(
    () =>
      debounce(searchFilters => {
        handleSearch(searchFilters)
      }, 500),
    [handleSearch]
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleFilterChange = (field, value) => {
    let newValue = value
    // Nếu field là year, chuyển từ moment object thành số
    if (field === 'year' && value) {
      newValue = value.year()
    }

    const newFilters = {
      ...filters,
      [field]: newValue,
    }
    setFilters(newFilters)

    if (field === 'title') {
      debouncedSearch(newFilters)
    } else {
      handleSearch(newFilters)
    }
  }

  // Xử lý dữ liệu ratings để hiển thị tên và ảnh người dùng
  const processedRatings = useMemo(() => {
    return ratings.map(rating => {
      const user = mentionUserList.find(
        user => user.username === rating.evaluateeName
      )
      if (user) {
        rating.image = user.image
        rating.display = user.display
      }
      return rating
    })
  }, [ratings, mentionUserList])

  const tableColumns = useMemo(
    () => [
      {
        title: 'STT',
        width: 20,
        render: (_, __, index) =>
          queryParams.page * queryParams.size + index + 1,
      },
      {
        title: 'Nội dung',
        width: 200,
        dataIndex: 'title',
      },
      {
        title: 'Tháng',
        width: 100,
        dataIndex: 'month',
      },
      {
        title: 'Năm',
        width: 100,
        dataIndex: 'year',
      },
      {
        title: 'Trạng thái',
        width: 100,
        align: 'center',
        render: record => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {StatusTag(TYPE_STATUS.INTERNAL_ASSESSMENT, record.status)}
          </div>
        ),
      },
      {
        title: 'Ngày tạo',
        width: 100,
        align: 'center',
        render: record => {
          const date = new Date(record?.createdAt)
          const formattedDay = String(date.getDate()).padStart(2, '0')
          const formattedMonth = String(date.getMonth() + 1).padStart(2, '0')
          const formattedYear = date.getFullYear()
          const formattedDate = `${formattedDay}-${formattedMonth}-${formattedYear}`
          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      {
        title: 'Người tạo',
        width: 150,
        render: record => (
          <p style={{ marginBottom: '0px' }}>
            <AvatarHasName
              size={28}
              imgId={record?.image}
              name={record?.display || 'LT'}
            />
          </p>
        ),
      },
    ],
    [queryParams.page, queryParams.size]
  )

  const handleOpenCreateAssessment = () => {
    setOpenCreateAssessment(true)
  }

  const onChangePage = async (pageNumber, pageSize) => {
    const updatedQueryParams = {
      ...internalAssessmentStore.queryParams,
      page: pageNumber - 1,
      size: pageSize,
    }

    runInAction(() => {
      internalAssessmentStore.queryParams = updatedQueryParams
    })

    loadingAnimationStore.setTableLoading(true)
    try {
      await fetchAllRatings()
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleFinish = async values => {
    try {
      setIsExporting(true)
      const month = values.selectedMonth.month() + 1
      const year = values.selectedMonth.year()
      const exportQueryParams = {
        page: 0,
        size: 200,
        year,
        month,
      }

      const data = await internalAssessmentStore.fetchExportData(
        exportQueryParams
      )

      await InternalAssessmentExportExcel({
        data,
        departmentListV2,
        month,
        year,
      })
      message.success('Xuất dữ liệu thành công!')

      setIsModalVisible(false)
      form.resetFields()
      setSelectedMonth(null)
    } catch (error) {
      message.error('Xuất dữ liệu thất bại!')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Đánh giá nội bộ | VIMC Đánh giá nội bộ VPRESS</title>
        </Helmet>
        <PageTitle location={props.location}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {userInOffice?.users?.some(
              u => u.user_code === currentUser?.code
            ) && (
              <Space>
                <Button
                  style={{ marginRight: '10px' }}
                  icon={<FileExcelOutlined />}
                  onClick={showModal}
                  disabled={isExporting}>
                  Xuất excel (.xlsx)
                </Button>
                <Modal
                  title="Xuất dữ liệu"
                  visible={isModalVisible}
                  onOk={() => form.submit()}
                  onCancel={handleCancel}
                  okText="Xuất excel (.xlsx)"
                  cancelText="Hủy bỏ"
                  okButtonProps={{
                    loading: isExporting,
                    disabled: isExporting,
                  }}>
                  <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <Form.Item
                      name="selectedMonth"
                      label="Chọn tháng/năm"
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn tháng/năm để xuất dữ liệu',
                        },
                      ]}>
                      <DatePicker
                        picker="month"
                        style={{ marginBottom: '10px', width: '100%' }}
                        format="MM-YYYY"
                        placeholder="Chọn tháng"
                        value={selectedMonth}
                        onChange={date => {
                          setSelectedMonth(date)
                          form.setFieldsValue({ selectedMonth: date })
                        }}
                        disabled={isExporting}
                      />
                    </Form.Item>
                  </Form>
                </Modal>
              </Space>
            )}
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.create__INTERNAL_ASSESSMENT}>
              <Button
                type={'primary'}
                icon={<PlusOutlined />}
                onClick={handleOpenCreateAssessment}>
                Tạo mới
              </Button>
            </AccessControlAction>
          </div>
        </PageTitle>
        <ContentBlockWrapper>
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item>
                <Input
                  allowClear
                  placeholder={'Tìm kiếm theo nội dung'}
                  onChange={e => handleFilterChange('title', e.target.value)}
                  value={filters.title}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="year">
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Chọn năm"
                  picker="year"
                  value={filters.year ? moment(filters.year, 'YYYY') : null}
                  onChange={value => handleFilterChange('year', value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item>
                <Select
                  allowClear
                  placeholder="Chọn tháng"
                  onChange={value => handleFilterChange('month', value)}
                  value={filters.month}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <Option
                      key={month}
                      value={month}>{`Tháng ${month}`}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item>
                <Select
                  allowClear
                  placeholder="Trạng thái"
                  onChange={value => handleFilterChange('status', value)}
                  value={filters.status}>
                  <Option value="PENDING">Chưa đánh giá</Option>
                  <Option value="TRUONG_BAN_APPROVED">
                    Trưởng ban đã đánh giá
                  </Option>
                  <Option value="COMPLETED">Đã có kết quả</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item>
                <Select
                  allowClear
                  placeholder="Chọn phòng ban"
                  onChange={value =>
                    handleFilterChange('departmentCode', value)
                  }
                  value={filters.departmentCode}>
                  {departmentListOnlyDepartment?.map(department => (
                    <Option key={department.code} value={department.code}>
                      {department.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <TableComponent
            rowKey={record => record.code}
            dataSource={processedRatings}
            columns={tableColumns}
            pagination={false}
            onRow={record => {
              return {
                onClick: event => {
                  event.stopPropagation()
                  history.push(`/internal-assessment/view/${record.code}`)
                },
              }
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Không có dữ liệu'}
                />
              ),
            }}
            loading={{
              tip: 'Đang tải phiếu đánh giá...',
              spinning: loadingAnimationStore.tableLoading,
            }}
          />
          <TableBottomPaginationBlock>
            <Pagination
              current={queryParams.page + 1}
              total={totalCount}
              size={size}
              onChange={onChangePage}
            />
          </TableBottomPaginationBlock>
        </ContentBlockWrapper>
      </DashboardLayout>
      {openCreateAssessment && (
        <InternalAssessmentCreate
          open={openCreateAssessment}
          setOpen={setOpenCreateAssessment}
        />
      )}
    </>
  )
}

export default inject(
  'internalAssessmentStore',
  'loadingAnimationStore',
  'userStore',
  'departmentStore',
  'authenticationStore'
)(observer(InternalAssessment))
